import GATSBY_COMPILED_MDX from "/vercel/path0/content/blog/re-introducing-the-pebble-appstore.mdx";
import React from 'react';
import {Link, graphql} from 'gatsby';
import {MDXProvider} from '@mdx-js/react';
import {Helmet} from 'react-helmet';
import Layout from '../components/Layout';
import DraftProtection from '../components/DraftProtection';
import {Comments} from '@hyvor/hyvor-talk-react';
const processTextForAsterisks = text => {
  if (typeof text !== 'string') return text;
  const parts = text.split('*');
  if (parts.length === 1) return text;
  return parts.reduce((acc, part, index) => {
    if (index === 0) return [part];
    return [...acc, React.createElement("sup", {
      key: index
    }, "*"), part];
  }, []);
};
const processChildren = children => {
  return React.Children.map(children, child => {
    if (typeof child === 'string') {
      return processTextForAsterisks(child);
    }
    if (React.isValidElement(child) && child.props.children) {
      return React.cloneElement(child, {
        ...child.props,
        children: processChildren(child.props.children)
      });
    }
    return child;
  });
};
const createProcessedComponent = (Component, className) => {
  return ({children, ...props}) => {
    const processedChildren = processChildren(children);
    return React.createElement(Component, Object.assign({}, props, {
      className: className
    }), processedChildren);
  };
};
const generateSlug = text => {
  if (!text) return '';
  const textContent = React.Children.toArray(text).map(child => {
    if (typeof child === 'string') return child;
    if (React.isValidElement(child) && child.props.children) {
      return child.props.children;
    }
    return '';
  }).join('');
  return textContent.toLowerCase().replace(/[^a-z0-9\s-]/g, '').replace(/\s+/g, '-').replace(/-+/g, '-').trim().substring(0, 20).replace(/-$/, '');
};
const H3WithAnchor = ({children, ...props}) => {
  const slug = generateSlug(children);
  const processedChildren = processChildren(children);
  return React.createElement("h3", Object.assign({}, props, {
    id: slug,
    className: "group relative"
  }), React.createElement("a", {
    href: `#${slug}`,
    className: "no-underline hover:no-underline !text-gray-900 dark:!text-gray-100 hover:!text-gray-900 dark:hover:!text-gray-100",
    onClick: e => {
      e.preventDefault();
      window.history.pushState(null, '', `#${slug}`);
      document.getElementById(slug)?.scrollIntoView({
        behavior: 'smooth'
      });
    }
  }, processedChildren, React.createElement("span", {
    className: "opacity-0 group-hover:opacity-100 ml-2 text-gray-400 dark:text-gray-500 transition-opacity"
  }, "#")));
};
const components = {
  p: props => {
    const text = props.children?.toString() || '';
    if (text.startsWith('[video](') && text.endsWith(')')) {
      const iframeMatch = text.match(/\[video\]\(([\s\S]*?)\)/);
      if (iframeMatch && iframeMatch[1]) {
        return React.createElement("div", {
          className: "relative w-full my-6 aspect-video overflow-hidden"
        }, React.createElement("div", {
          className: "absolute inset-0 [&>iframe]:w-full [&>iframe]:h-full",
          dangerouslySetInnerHTML: {
            __html: iframeMatch[1].trim()
          }
        }));
      }
    }
    if (text === 'mp4-video') {
      const videoSrc = props.children?.props?.href;
      if (videoSrc) {
        return React.createElement("div", {
          className: "relative w-full my-6"
        }, React.createElement("video", {
          className: "w-full",
          controls: true,
          preload: "metadata",
          playsInline: true
        }, React.createElement("source", {
          src: videoSrc,
          type: "video/mp4"
        }), "Your browser does not support the video tag."));
      }
    }
    if (text.startsWith('[mp4-video](') && text.endsWith(')')) {
      const videoMatch = text.match(/\[mp4-video\]\(([\s\S]*?)\)/);
      if (videoMatch && videoMatch[1]) {
        const videoSrc = videoMatch[1].trim();
        return React.createElement("div", {
          className: "relative w-full my-6"
        }, React.createElement("video", {
          className: "w-full",
          controls: true,
          preload: "metadata",
          playsInline: true
        }, React.createElement("source", {
          src: videoSrc,
          type: "video/mp4"
        }), "Your browser does not support the video tag."));
      }
    }
    return React.createElement("p", props, processChildren(props.children));
  },
  a: ({children, ...props}) => {
    const isExternal = props.href && !props.href.startsWith('/') && !props.href.startsWith('#') && !props.href.includes('ericmigi.com');
    return React.createElement("a", Object.assign({}, props, {
      className: "text-indigo-600 dark:text-indigo-400 hover:text-indigo-800 dark:hover:text-indigo-300"
    }, isExternal && ({
      target: '_blank',
      rel: 'noopener noreferrer'
    })), processChildren(children));
  },
  img: props => React.createElement("img", Object.assign({}, props, {
    className: "rounded-lg my-6 w-full"
  })),
  h1: createProcessedComponent('h1'),
  h2: createProcessedComponent('h2'),
  h3: H3WithAnchor,
  h4: createProcessedComponent('h4'),
  h5: createProcessedComponent('h5'),
  h6: createProcessedComponent('h6'),
  li: createProcessedComponent('li'),
  strong: createProcessedComponent('strong'),
  em: createProcessedComponent('em'),
  blockquote: createProcessedComponent('blockquote'),
  pre: ({children, ...props}) => React.createElement("pre", Object.assign({}, props, {
    className: "bg-gray-900 dark:bg-gray-800 text-gray-100 dark:text-gray-100 p-6 rounded-xl my-8 overflow-x-auto shadow-lg"
  }), children),
  code: ({children, ...props}) => {
    const isInline = !props.className;
    return React.createElement("code", Object.assign({}, props, {
      className: isInline ? "bg-gray-100 dark:bg-gray-800 text-gray-900 dark:text-gray-100 px-1.5 py-0.5 rounded text-sm font-mono" : props.className
    }), processChildren(children));
  }
};
const PostTemplate = ({data, children, location}) => {
  const {frontmatter} = data.mdx;
  const structuredData = {
    '@context': 'https://schema.org',
    '@type': 'BlogPosting',
    headline: frontmatter.title,
    description: frontmatter.subtitle || frontmatter.title,
    image: `https://ericmigi.com${frontmatter.socialImage || '/indexsocial.png'}`,
    datePublished: frontmatter.date,
    dateModified: frontmatter.lastEditedTime || frontmatter.date,
    author: {
      '@type': 'Person',
      name: 'Eric Migicovsky',
      url: 'https://ericmigi.com'
    },
    publisher: {
      '@type': 'Person',
      name: 'Eric Migicovsky',
      url: 'https://ericmigi.com'
    },
    mainEntityOfPage: {
      '@type': 'WebPage',
      '@id': `https://ericmigi.com/blog/${frontmatter.slug}`
    }
  };
  const content = React.createElement("article", null, React.createElement(Link, {
    to: "/",
    className: "text-indigo-600 dark:text-indigo-400 hover:text-indigo-800 dark:hover:text-indigo-300 mb-8 block"
  }, "← Back to Home"), frontmatter.externalUrl && React.createElement("div", {
    className: "mb-8 text-sm text-gray-600 dark:text-gray-400"
  }, "Originally posted on", ' ', React.createElement("a", {
    href: frontmatter.externalUrl,
    target: "_blank",
    rel: "noopener noreferrer",
    className: "text-indigo-600 dark:text-indigo-400 hover:text-indigo-800 dark:hover:text-indigo-300"
  }, new URL(frontmatter.externalUrl).hostname)), React.createElement("h1", {
    className: "text-3xl font-bold mb-2 font-title text-gray-900 dark:text-gray-100"
  }, processTextForAsterisks(frontmatter.title)), React.createElement("div", {
    className: "text-sm text-gray-600 dark:text-gray-400 mb-8"
  }, "[", frontmatter.date, "]"), React.createElement("div", {
    className: "prose prose-sm dark:prose-invert max-w-none [&_.caption]:text-center [&_.caption]:text-sm [&_.caption]:text-gray-600 dark:[&_.caption]:text-gray-400 [&_.caption]:-mt-4 [&_.caption]:mb-6"
  }, React.createElement(MDXProvider, {
    components: components
  }, children)), frontmatter.comments && frontmatter.comments === 'hyvortalk' ? React.createElement("div", {
    className: "mt-8 pt-8 border-t border-gray-200 dark:border-gray-700"
  }, React.createElement(Comments, {
    "website-id": process.env.GATSBY_HYVOR_TALK_WEBSITE_ID,
    "page-id": frontmatter.slug
  })) : frontmatter.comments && React.createElement("div", {
    className: "mt-8 pt-8 border-t border-gray-200 dark:border-gray-700"
  }, React.createElement("a", {
    href: frontmatter.comments,
    target: "_blank",
    rel: "noopener noreferrer",
    className: "text-indigo-600 dark:text-indigo-400 hover:text-indigo-800 dark:hover:text-indigo-300"
  }, "Comment on Hacker News")));
  return React.createElement(Layout, {
    location: location
  }, React.createElement(Helmet, {
    title: frontmatter.title,
    link: [{
      rel: 'icon',
      href: '/bc-notes.png'
    }, {
      rel: 'canonical',
      href: `https://ericmigi.com/blog/${frontmatter.slug}`
    }],
    script: [{
      type: 'application/ld+json',
      innerHTML: JSON.stringify(structuredData)
    }],
    meta: [{
      name: 'title',
      content: frontmatter.title
    }, {
      name: 'description',
      content: frontmatter.subtitle || frontmatter.title
    }, {
      name: 'author',
      content: 'Eric Migicovsky'
    }, {
      property: 'og:type',
      content: 'article'
    }, {
      property: 'og:url',
      content: `https://ericmigi.com/blog/${frontmatter.slug}`
    }, {
      property: 'og:title',
      content: frontmatter.title
    }, {
      property: 'og:description',
      content: frontmatter.subtitle || frontmatter.title
    }, {
      property: 'og:image',
      content: `https://ericmigi.com${frontmatter.socialImage ? frontmatter.socialImage : '/indexsocial.png'}`
    }, {
      property: 'og:image:width',
      content: '1200'
    }, {
      property: 'og:image:height',
      content: '630'
    }, {
      property: 'article:published_time',
      content: frontmatter.date
    }, {
      property: 'article:modified_time',
      content: frontmatter.lastEditedTime || frontmatter.date
    }, {
      property: 'article:author',
      content: 'Eric Migicovsky'
    }, {
      name: 'twitter:card',
      content: 'summary_large_image'
    }, {
      name: 'twitter:url',
      content: `https://ericmigi.com/blog/${frontmatter.slug}`
    }, {
      name: 'twitter:title',
      content: frontmatter.title
    }, {
      name: 'twitter:description',
      content: frontmatter.subtitle || frontmatter.title
    }, {
      name: 'twitter:image',
      content: `https://ericmigi.com${frontmatter.socialImage ? frontmatter.socialImage : '/indexsocial.png'}`
    }, {
      name: 'twitter:image:alt',
      content: frontmatter.title
    }, {
      name: 'twitter:creator',
      content: '@ericmigi'
    }].filter(() => frontmatter.type !== 'external')
  }), React.createElement("div", {
    className: "max-w-xl"
  }, frontmatter.draft ? React.createElement(DraftProtection, null, content) : content));
};
export const query = graphql`
  query($id: String!) {
    mdx(id: { eq: $id }) {
      frontmatter {
        title
        date(formatString: "YYYY-MM-DD")
        type
        subtitle
        slug
        socialImage
        externalUrl
        comments
        draft
        lastEditedTime
      }
    }
  }
`;
PostTemplate
export default function GatsbyMDXWrapper(props) {
  return React.createElement(PostTemplate, props, React.createElement(GATSBY_COMPILED_MDX, props));
}
